import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorBox } from '../components/Feedback.js';
import { useRedefinirSenha } from '../api/queries.js';

/**
 * Onde o link do e-mail cai.
 *
 * Vive **fora** do portão de autenticação, e isso é obrigatório: quem clica
 * neste link é exatamente quem não consegue entrar. Pedir senha para chegar
 * até aqui seria pedir a senha de quem veio porque esqueceu a senha.
 *
 * O token chega no **fragmento** (`#token=...`), nunca na query — ele não é
 * enviado em requisição nenhuma, então não cai no log do nginx, no OpenSearch
 * nem é visto pela Cloudflare. Ver `domain/recuperacaoDeSenha.ts`.
 */
export function RedefinirSenha() {
  const navigate = useNavigate();
  const redefinir = useRedefinirSenha();

  const { hash } = useLocation();
  const token = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash).get('token') ?? '';

  const [senha, setSenha] = useState('');
  const [repetida, setRepetida] = useState('');

  /*
   * A confirmação existe só aqui, e não na troca de senha comum.
   *
   * Lá, quem erra a digitação descobre no próximo login e ainda tem a senha
   * antiga para pedir outra troca. Aqui o token é de uso único: errar significa
   * ficar com uma senha que ninguém sabe qual é, e ter de recomeçar pelo
   * e-mail. O campo a mais custa um toque e evita o beco.
   */
  const divergem = repetida.length > 0 && senha !== repetida;

  if (!token) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-4 px-5">
        <h1 className="text-center text-xl font-semibold">Link incompleto</h1>
        <p className="text-center text-sm text-adega-muted">
          Este endereço não traz o código de redefinição. Abra o link direto do e-mail, sem copiar
          só um pedaço dele.
        </p>
        <button type="button" className="btn-primary" onClick={() => navigate('/')}>
          Ir para a entrada
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-5 py-10">
      <header className="mb-6 text-center">
        <p aria-hidden className="text-5xl">
          🍷
        </p>
        <h1 className="mt-3 text-2xl font-semibold">Nova senha</h1>
        <p className="mt-1 text-sm text-adega-muted">Escolha uma senha para entrar na sua adega.</p>
      </header>

      <form
        className="card space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          /*
           * Vai direto para a entrada, com o aviso lá.
           *
           * A primeira versão mostrava uma tela de sucesso aqui e um botão
           * "Entrar". O dono redefiniu a senha e a tela voltou para o
           * formulário vazio — não consegui reproduzir, e uma tela de
           * confirmação que depende de estado do cliente sobreviver é frágil
           * por natureza.
           *
           * Ir para o login não é só mais robusto, é melhor: uma batida a menos,
           * e a pessoa cai exatamente onde precisa agir. A mensagem não se
           * perde, viaja no `state` da navegação.
           *
           * `replace` porque voltar para cá seria voltar para um token já
           * queimado — o botão "voltar" cairia num formulário que só responde
           * "esta senha já foi redefinida".
           */
          void redefinir
            .mutateAsync({ token, novaSenha: senha })
            .then(() => navigate('/', { replace: true, state: { senhaRedefinida: true } }));
        }}
      >
        <div>
          <label className="label" htmlFor="senha">
            Nova senha
          </label>
          <input
            id="senha"
            type="password"
            autoComplete="new-password"
            className="field"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="Pelo menos 8 caracteres"
          />
        </div>

        <div>
          <label className="label" htmlFor="repetida">
            Repita a nova senha
          </label>
          <input
            id="repetida"
            type="password"
            autoComplete="new-password"
            className="field"
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
            placeholder="A mesma de cima"
          />
          {divergem ? <p className="mt-1 text-xs text-adega-wine-light">As duas não batem.</p> : null}
        </div>

        {redefinir.error ? <ErrorBox erro={redefinir.error} /> : null}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={redefinir.isPending || senha.length === 0 || senha !== repetida}
        >
          {redefinir.isPending ? 'Aguarde…' : 'Salvar nova senha'}
        </button>
      </form>

      <p className="mt-6 text-center text-xs text-adega-muted">
        O link vale por uma hora e só funciona uma vez.
      </p>
    </div>
  );
}
